import React from 'react'
import { useTranslation } from 'react-i18next'
import CookiePreferencesModal from '../CookieConsent/CookiePreferencesModal'
import { useCookieConsent } from '../../hooks/useCookieConsent'
import { LanguageSwitcher } from '../shared'

/**
 * Footer Component
 * Provides links to legal pages and cookie preferences
 * Matches the app's design system with subtle styling
 */
const Footer = () => {
  const { t, i18n } = useTranslation('common')
  const { openPreferences, closePreferences, showPreferences, updatePreferences } = useCookieConsent()
  const currentYear = new Date().getFullYear()
  const currentLang = i18n.language

  const handleSaveCookiePreferences = (preferences) => {
    updatePreferences(preferences)
  }

  const handleCookieSettingsClick = (e) => {
    e.preventDefault()
    openPreferences()
  }

  return (
    <>
      {/* Footer */}
      <footer
        className="
          bg-white border-t border-gray-200
          mt-auto
        "
        role="contentinfo"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6 sm:py-8">
            {/* Main Footer Content */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              {/* Company Info */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                {/* Logo/Brand */}
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-gradient-to-r from-purple-600 to-pink-500 rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold text-sm">SP</span>
                  </div>
                  <span className="font-semibold text-gray-900">
                    {t('brand.name')}
                  </span>
                </div>

                {/* Copyright */}
                <span className="text-sm text-gray-500 sm:border-l sm:border-gray-300 sm:pl-4">
                  © {currentYear} {t('footer.copyright', 'All rights reserved')}
                </span>
              </div>

              {/* Links Section */}
              <div className="flex flex-wrap items-center gap-4 sm:gap-6 text-sm">
                {/* Legal Links */}
                <a
                  href={currentLang === 'de' ? '/impressum' : '/imprint'}
                  className="
                    text-gray-600 hover:text-purple-600
                    transition-colors duration-200
                    font-medium
                  "
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {currentLang === 'de' ? 'Impressum' : 'Imprint'}
                </a>

                <a
                  href={currentLang === 'de' ? '/datenschutz' : '/privacy'}
                  className="
                    text-gray-600 hover:text-purple-600
                    transition-colors duration-200
                    font-medium
                  "
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {currentLang === 'de' ? 'Datenschutz' : 'Privacy Policy'}
                </a>

                {/* Cookie Settings - Interactive Link */}
                <button
                  onClick={handleCookieSettingsClick}
                  className="
                    text-gray-600 hover:text-purple-600
                    transition-colors duration-200
                    font-medium
                    inline-flex items-center gap-1.5
                    group
                  "
                  aria-label={t('footer.cookieSettings.ariaLabel', 'Open cookie preferences')}
                >
                  <span className="text-base group-hover:animate-pulse">🍪</span>
                  <span>
                    {t('footer.cookieSettings', 'Cookie Settings')}
                  </span>
                </button>

                {/* Terms of Service */}
                <a
                  href="/terms"
                  className="
                    text-gray-600 hover:text-purple-600
                    transition-colors duration-200
                    font-medium
                  "
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('footer.terms', 'Terms')}
                </a>

                {/* AVV */}
                <a
                  href="/avv"
                  className="
                    text-gray-600 hover:text-purple-600
                    transition-colors duration-200
                    font-medium
                  "
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  AVV
                </a>

                {/* Contact */}
                <a
                  href="/contact"
                  className="
                    text-gray-600 hover:text-purple-600
                    transition-colors duration-200
                    font-medium
                  "
                >
                  {t('footer.contact', 'Contact')}
                </a>
              </div>
            </div>

            {/* Language Switcher */}
            <div className="mt-6 pt-6 border-t border-gray-100 flex items-center justify-center gap-2">
              <span className="text-xs text-gray-500">
                {t('footer.language', 'Language')}:
              </span>
              <LanguageSwitcher variant="compact" dropdownAlign="right" />
            </div>
          </div>
        </div>
      </footer>

      {/* Cookie Preferences Modal */}
      <CookiePreferencesModal
        isOpen={showPreferences}
        onClose={closePreferences}
        onSave={handleSaveCookiePreferences}
      />
    </>
  )
}

export default Footer